import {Action, Module, Mutation} from 'vuex-module-decorators'
import store, {alertModule, currentUserModule, studyModule} from "@/store";
import {MdbsCore} from "@/api/tsclient";
import BaseModule from "@/store/modules/BaseModule";
import ISafetyReportResponseDto = MdbsCore.ISafetyReportResponseDto;
import SafetyReportResponseDto = MdbsCore.SafetyReportResponseDto;
import SafetyReportConfigurationUpdateDto = MdbsCore.SafetyReportConfigurationUpdateDto;
import IEventReportOverviewDto = MdbsCore.IEventReportOverviewDto;
import EventReportOverviewDto = MdbsCore.EventReportOverviewDto;
import IEventReportFieldDto = MdbsCore.IEventReportFieldDto;
import ISafetyReportConfigurationDto = MdbsCore.ISafetyReportConfigurationDto;
import SafetyReportConfigurationDto = MdbsCore.SafetyReportConfigurationDto;

const safetyClient = new MdbsCore.SafetyClient();
const eventReportClient = new MdbsCore.EventReportClient();

@Module({namespaced: true, name: 'safety', store: store})
export default class SafetyModule extends BaseModule {
    private _safetyFreeTextReport: ISafetyReportResponseDto = new SafetyReportResponseDto();
    private _safetyScreeningReport: ISafetyReportResponseDto = new SafetyReportResponseDto();
    private _safetyReportConfiguration: ISafetyReportConfigurationDto = new SafetyReportConfigurationDto();
    private _eventReportOverview: IEventReportOverviewDto = new EventReportOverviewDto();
    private _eventReportFields: Array<IEventReportFieldDto> = [];

    public get SafetyFreeTextReport() {
        return this._safetyFreeTextReport;
    }

    public get SafetyScreeningReport() {
        return this._safetyScreeningReport;
    }

    public get SafetyReportConfiguration(): ISafetyReportConfigurationDto {
        return this._safetyReportConfiguration;
    }

    public get EventReportOverview() {
        return this._eventReportOverview;
    }

    public get EventReportFields() {
        return this._eventReportFields;
    }

    @Mutation
    reset() {
        this._safetyFreeTextReport = new SafetyReportResponseDto();
        this._safetyScreeningReport = new SafetyReportResponseDto();
        this._safetyReportConfiguration = new SafetyReportConfigurationDto();
    }

    @Mutation
    setSafetyFreeTextReport(safetyReport: ISafetyReportResponseDto) {
        this._safetyFreeTextReport = safetyReport;
    }

    @Mutation
    setSafetyScreeningReport(safetyScreeningReport: ISafetyReportResponseDto) {
        this._safetyScreeningReport = safetyScreeningReport;
    }

    @Mutation
    setSafetyReportConfiguration(configuration: ISafetyReportConfigurationDto) {
        this._safetyReportConfiguration = configuration;
    }

    @Mutation
    setEventReportOverview(eventReportOverview: IEventReportOverviewDto) {
        this._eventReportOverview = eventReportOverview;
    }

    @Mutation
    setEventReportFields(eventReportFields: Array<IEventReportFieldDto>) {
        this._eventReportFields = eventReportFields;
    }

    @Action({rawError: true})
    async loadSafetyFreeTextReport(payload: { fromDate: Date }) {
        await this.execute(async () => {
            const safetyReport = await safetyClient.getSafetyReportForStudy(studyModule.study.id, payload.fromDate);

            this.setSafetyFreeTextReport(safetyReport);
        }).catch();
    }

    @Action({rawError: true})
    async loadSafetyScreeningReport(payload: { fromDate: Date, toDate: Date}) {
        await this.execute(async () => {
            const safetyReport = await safetyClient.getSafetyScreeningReportForStudy(studyModule.study.id, payload.fromDate, payload.toDate);

            this.setSafetyScreeningReport(safetyReport);
        }).catch();
    }

    @Action({rawError: true})
    async loadSafetyReportConfiguration() {
        await this.execute(async () => {
            if (studyModule.study.id == undefined) {
                await currentUserModule.load();
            }

            const configuration = await safetyClient.getSafetyReportConfiguration(studyModule.study.id);
            this.setSafetyReportConfiguration(configuration);
        }).catch();
    }

    @Action({rawError: true})
    async saveSafetyReportConfiguration(payload: { configuration: SafetyReportConfigurationUpdateDto }) {
        await this.execute(async () => {
            await safetyClient.putSafetyReportConfiguration(studyModule.study.id, payload.configuration);
        }).catch();
    }

    @Action({rawError: true})
    async loadEventReportOverview() {
        await this.execute(async () => {
            if (studyModule.study.id == undefined) await currentUserModule.load();
            const safetyOverview = await safetyClient.getEventReportOverviewForStudy(studyModule.study.id);

            this.setEventReportOverview(safetyOverview);
            // console.dir(safetyOverview.eventReports?.filter(er => er.eventName == "x11111"));
        }).catch();
    }

    @Action({rawError: true})
    async loadEventReportFields(payload: { reportId: string }) {
        await this.execute(async () => {
            const eventReportFields = await eventReportClient.getEventReportFields(payload.reportId);

            this.setEventReportFields(eventReportFields);
        })
            .catch();
    }

    @Action({rawError: true})
    async getSignatureById(payload: { id: string }) {
        this.startLoading();
        try {
            return await safetyClient.getSignatureById(payload.id);
        } catch (e) {
            console.error("signature: get by id", e);
            await alertModule.handleException(e);
        } finally {
            this.stopLoading();
        }
        return null;
    }

    @Action({rawError: true})
    async getSignatureByReferenceId(payload: { referenceId: string }) {
        this.startLoading();
        try {
            return await safetyClient.getSignatureByReferenceId(payload.referenceId);
        } catch (e) {
            console.error("signature: get by reference id", e);
            await alertModule.handleException(e);
        } finally {
            this.stopLoading();
        }
        return null;
    }
}
